/**
 * Browser-based code sandbox to replace container execution
 * Uses iframe with sandboxed attributes for secure code execution
 */

import type { FileType } from '../../routes/chat/hooks/use-chat';

export interface SandboxFile {
  path: string;
  content: string;
  type: 'html' | 'css' | 'js' | 'json' | 'md' | 'other';
}

export interface SandboxPreview {
  url: string;
  iframe: HTMLIFrameElement;
  files: Map<string, SandboxFile>;
}

export interface SandboxError {
  message: string;
  line?: number;
  column?: number;
  filename?: string;
  stack?: string;
}

export class BrowserSandboxService {
  private previews = new Map<string, SandboxPreview>();
  private serviceWorkerRegistered = false;

  constructor() {
    this.initializeServiceWorker();
  }

  /**
   * Initialize service worker for file serving
   */
  private async initializeServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator && !this.serviceWorkerRegistered) {
      try {
        const registration = await navigator.serviceWorker.register('/sandbox-sw.js');
        console.log('Sandbox service worker registered:', registration);
        this.serviceWorkerRegistered = true;
      } catch (error) {
        console.warn('Failed to register sandbox service worker:', error);
      }
    }
  }

  /**
   * Create a new sandbox preview
   */
  async createPreview(
    sandboxId: string, 
    files: FileType[], 
    options: {
      width?: number;
      height?: number;
      autoReload?: boolean;
    } = {}
  ): Promise<SandboxPreview> {
    const { width = 800, height = 600 } = options;

    // Convert files to sandbox format
    const sandboxFiles = new Map<string, SandboxFile>();
    for (const file of files) {
      sandboxFiles.set(file.filePath, {
        path: file.filePath,
        content: file.fileContents,
        type: this.getFileType(file.filePath)
      });
    }

    // Create iframe
    const iframe = document.createElement('iframe');
    iframe.style.width = `${width}px`;
    iframe.style.height = `${height}px`;
    iframe.style.border = '1px solid #ccc';
    iframe.style.borderRadius = '4px';
    
    // Sandbox attributes for security
    iframe.sandbox.add(
      'allow-scripts',
      'allow-same-origin',
      'allow-forms',
      'allow-popups',
      'allow-modals'
    );

    // Generate preview URL
    const previewUrl = this.generatePreviewUrl(sandboxId);
    
    // Store files in service worker cache
    await this.storeFilesInCache(sandboxId, sandboxFiles);
    
    // Set iframe source
    iframe.src = previewUrl;

    const preview: SandboxPreview = {
      url: previewUrl,
      iframe,
      files: sandboxFiles
    };

    this.previews.set(sandboxId, preview);
    return preview;
  }

  /**
   * Update files in an existing preview
   */
  async updatePreview(sandboxId: string, files: FileType[]): Promise<void> {
    const preview = this.previews.get(sandboxId);
    if (!preview) {
      throw new Error(`Preview not found: ${sandboxId}`);
    }

    // Update files
    preview.files.clear();
    for (const file of files) {
      preview.files.set(file.filePath, {
        path: file.filePath,
        content: file.fileContents,
        type: this.getFileType(file.filePath)
      });
    }

    // Update cache
    await this.storeFilesInCache(sandboxId, preview.files);

    // Reload iframe by reassigning the src
    const currentSrc = preview.iframe.src;
    preview.iframe.src = currentSrc;
  }

  /**
   * Get preview by sandbox ID
   */
  getPreview(sandboxId: string): SandboxPreview | null {
    return this.previews.get(sandboxId) || null;
  }

  /**
   * Delete a preview
   */
  async deletePreview(sandboxId: string): Promise<void> {
    const preview = this.previews.get(sandboxId);
    if (preview) {
      // Remove iframe from DOM if it's attached
      if (preview.iframe.parentNode) {
        preview.iframe.parentNode.removeChild(preview.iframe);
      }
      
      // Clear cache
      await this.clearCache(sandboxId);
      
      this.previews.delete(sandboxId);
    }
  }

  /**
   * Execute JavaScript code in sandbox
   */
  async executeCode(sandboxId: string, code: string): Promise<{
    result?: unknown;
    error?: SandboxError;
  }> {
    const preview = this.previews.get(sandboxId);
    if (!preview) {
      throw new Error(`Preview not found: ${sandboxId}`);
    }

    return new Promise((resolve) => {
      const messageHandler = (event: MessageEvent) => {
        if (event.source === preview.iframe.contentWindow) {
          window.removeEventListener('message', messageHandler);
          resolve(event.data);
        }
      };

      window.addEventListener('message', messageHandler);

      // Send code to iframe for execution
      preview.iframe.contentWindow?.postMessage({
        type: 'execute',
        code
      }, '*');

      // Timeout after 5 seconds
      setTimeout(() => {
        window.removeEventListener('message', messageHandler);
        resolve({ error: { message: 'Execution timeout' } });
      }, 5000);
    });
  }

  /**
   * Get console logs from sandbox
   */
  async getConsoleLogs(sandboxId: string): Promise<Array<{
    level: 'log' | 'warn' | 'error' | 'info';
    message: string;
    timestamp: number;
  }>> {
    const preview = this.previews.get(sandboxId);
    if (!preview) {
      return [];
    }

    return new Promise((resolve) => {
      const messageHandler = (event: MessageEvent) => {
        if (event.source === preview.iframe.contentWindow && event.data.type === 'console-logs') {
          window.removeEventListener('message', messageHandler);
          resolve(event.data.logs);
        }
      };

      window.addEventListener('message', messageHandler);
      preview.iframe.contentWindow?.postMessage({ type: 'get-console-logs' }, '*');

      setTimeout(() => {
        window.removeEventListener('message', messageHandler);
        resolve([]);
      }, 1000);
    });
  }

  /**
   * Create a downloadable bundle of files
   */
  async createDownloadBundle(sandboxId: string): Promise<Blob> {
    const preview = this.previews.get(sandboxId);
    if (!preview) {
      throw new Error(`Preview not found: ${sandboxId}`);
    }

    // Create a simple ZIP-like structure (for demo purposes)
    // In production, you'd use a proper ZIP library like JSZip
    const files: Array<{ name: string; content: string }> = [];
    
    for (const [path, file] of preview.files) {
      files.push({
        name: path,
        content: file.content
      });
    }

    const bundleContent = JSON.stringify(files, null, 2);
    return new Blob([bundleContent], { type: 'application/json' });
  }

  /**
   * Get sandbox statistics
   */
  getStats(): {
    totalPreviews: number;
    totalFiles: number;
    memoryUsage: number;
  } {
    let totalFiles = 0;
    let memoryUsage = 0;

    for (const preview of this.previews.values()) {
      totalFiles += preview.files.size;
      for (const file of preview.files.values()) {
        memoryUsage += new Blob([file.content]).size;
      }
    }

    return {
      totalPreviews: this.previews.size,
      totalFiles,
      memoryUsage
    };
  }

  // Private methods

  private generatePreviewUrl(sandboxId: string): string {
    return `/sandbox/${sandboxId}/index.html`;
  }

  private getFileType(filePath: string): SandboxFile['type'] {
    const ext = filePath.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'html':
      case 'htm':
        return 'html';
      case 'css':
        return 'css';
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx':
        return 'js';
      case 'json':
        return 'json';
      case 'md':
      case 'markdown':
        return 'md';
      default:
        return 'other';
    }
  }

  private async storeFilesInCache(sandboxId: string, files: Map<string, SandboxFile>): Promise<void> {
    if (!this.serviceWorkerRegistered) {
      console.warn('Service worker not registered, files will not be cached');
      return;
    }

    // Send files to service worker for caching
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'cache-files',
        sandboxId,
        files: Array.from(files.values())
      });
    }
  }

  private async clearCache(sandboxId: string): Promise<void> {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'clear-cache',
        sandboxId
      });
    }
  }
}

// Export singleton instance
export const browserSandbox = new BrowserSandboxService();
